const { submitProductOrder } = await import(window.__wearseProductOrderModule);

(() => {
  function formatMoney(cents, currencyCode) {
    const amount = Number(cents || 0) / 100;
    return `${amount.toLocaleString(undefined, {
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    })} ${currencyCode || ''}`.trim();
  }

  function updateDetails(panel, variant, sectionId) {
    if (!panel || !variant) return;

    const price = panel.querySelector('[data-wearse-detail-price]');
    if (price) {
      const currencyCode = panel.closest('product-info')?.querySelector('[data-dynamic-offer]')?.dataset.currency || document.querySelector(`#variant-selects-${sectionId}`)?.dataset.currencyCode;
      price.textContent = formatMoney(variant.price, currencyCode);
    }

    panel.querySelectorAll('[data-wearse-detail-option-row]').forEach((row) => {
      const index = Number(row.dataset.optionIndex || 0);
      const value = variant.options?.[index];
      row.hidden = !value;
      const cell = row.querySelector('[data-wearse-detail-option]');
      if (cell && value) cell.textContent = value;
    });

    const skuRow = panel.querySelector('[data-wearse-detail-sku-row]');
    const sku = panel.querySelector('[data-wearse-detail-sku]');
    if (skuRow && sku) {
      skuRow.hidden = !variant.sku;
      sku.textContent = variant.sku || '';
    }

    const availability = panel.querySelector('[data-wearse-detail-availability]');
    if (availability) availability.textContent = variant.available ? 'Disponible' : 'Indisponible';
  }

  function getDirectOrderVariant(form, variantId) {
    try {
      const variants = JSON.parse(form.querySelector('[data-wearse-direct-order-variants]')?.textContent || '[]');
      return variants.find((variant) => String(variant.id) === String(variantId)) || null;
    } catch {
      return null;
    }
  }

  function getDirectOrderVariants(form) {
    try {
      return JSON.parse(form.querySelector('[data-wearse-direct-order-variants]')?.textContent || '[]');
    } catch {
      return [];
    }
  }

  function getVariantSelections(row) {
    return [...row.querySelectorAll('[data-option-index]')].reduce((values, input) => {
      if (input.tagName === 'SELECT' || input.checked) values[Number(input.dataset.optionIndex)] = input.value;
      return values;
    }, []);
  }

  function findSelectedVariant(variants, selections) {
    return variants.find((variant) =>
      variant.available && variant.options?.every((value, index) => value === selections[index])
    );
  }

  function selectedOrderVariants(form) {
    const variants = getDirectOrderVariants(form);
    return [...form.querySelectorAll('[data-wearse-direct-order-variant-row]')]
      .map(row => row.dataset.defaultVariant ? variants.find(v => String(v.id) === row.dataset.defaultVariant && v.available) : findSelectedVariant(variants, getVariantSelections(row)));
  }

  function offerTotals(form, variants) {
    const normal = variants.reduce((sum, variant) => sum + Number(variant?.price || 0), 0);
    const configured = String(form.dataset.offerPrices || '').split(',').map(Number);
    const configuredTotal = configured[variants.length - 1];
    const percent = Math.min(100, Math.max(0, Number(form.dataset.discountPercent) || 0));
    const discount = Number.isFinite(configuredTotal)
      ? Math.max(0, normal - configuredTotal)
      : variants.length === 2
        ? Math.min(normal, percent > 0 ? Math.round(normal * percent / 100) : Number(form.dataset.legacyDiscount) || 0)
        : 0;
    return { normal, discount, total: normal - discount, percent };
  }

  function hasOrderStock(form, variants) {
    if (!variants.length || variants.some(variant => !variant?.available)) return false;
    let inventory = {};
    try { inventory = JSON.parse(form.querySelector('[data-wearse-inventory]')?.textContent || '{}'); } catch { return false; }
    const counts = new Map();
    for (const variant of variants) {
      counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
      const stock = inventory[variant.id];
      if (stock?.tracked && counts.get(variant.id) > Number(stock.quantity)) return false;
    }
    return true;
  }

  function updateOrderRecap(form) {
    const target = form.querySelector('[data-order-recap]');
    if (!target) return;
    const variants = selectedOrderVariants(form);
    const totals = offerTotals(form, variants);
    const currency = form.dataset.currency || 'MAD';
    const optionNames = String(form.dataset.optionNames || '').split('||');
    const rows = [
      ['Offre', `${variants.length} ${form.dataset.offerLabel}${variants.length > 1 ? 's' : ''}`],
      ['Produit', form.dataset.productTitle],
      ...variants.map((variant, index) => [
        `${form.dataset.offerLabel === 'paire' ? 'Paire' : 'Article'} ${index + 1}`,
        variant ? variant.options.map((value, optionIndex) => value === 'Default Title' ? '' : `${optionNames[optionIndex]} : ${value}`).filter(Boolean).join(' · ') || variant.title : 'Indisponible',
      ]),
      ['Quantité', String(variants.length)],
    ];
    if (variants.length === 2 && totals.discount > 0) {
      rows.push(['Prix normal', formatMoney(totals.normal, currency)]);
      rows.push(['Réduction', `− ${formatMoney(totals.discount, currency)}`]);
    }
    rows.push(['Total', variants.every(Boolean) ? formatMoney(totals.total, currency) : 'Indisponible']);
    const heading = document.createElement('h3');
    heading.textContent = 'Résumé de votre commande';
    const list = document.createElement('dl');
    rows.forEach(([label, value]) => {
      const row = document.createElement('div');
      const term = document.createElement('dt');
      const detail = document.createElement('dd');
      term.textContent = label;
      detail.textContent = value;
      row.append(term, detail);
      list.append(row);
    });
    target.replaceChildren(heading, list);
  }

  function showOrderStep(form, step, focus = true) {
    if (!form.querySelector('[data-order-step]')) return;
    form.dataset.orderStep = String(step);
    form.querySelectorAll('[data-order-step]').forEach(panel => { panel.hidden = panel.dataset.orderStep !== String(step); });
    form.querySelectorAll('[data-order-indicator]').forEach(indicator => {
      if (indicator.dataset.orderIndicator === String(step)) indicator.setAttribute('aria-current', 'step');
      else indicator.removeAttribute('aria-current');
    });
    updateOrderRecap(form);
    if (focus) {
      const target = form.querySelector(step === 2 ? '[data-order-heading]' : '[data-order-continue]');
      target?.focus({ preventScroll: true });
      form.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    }
  }

  function continueOrder(form) {
    if (form.dataset.submitting === 'true') return;
    const feedback = form.querySelector('[data-wearse-direct-order-feedback]');
    if (!updateVariantRows(form)) {
      feedback.textContent = 'Veuillez choisir des variantes disponibles pour chaque paire.';
      feedback.classList.add('is-error');
      feedback.hidden = false;
      return;
    }
    feedback.hidden = true;
    showOrderStep(form, 2);
  }

  function updateOfferDisplay(form) {
    if (!form.hasAttribute('data-dynamic-offer')) return;
    const selected = selectedOrderVariants(form);
    const first = selected[0];
    const second = selected[1] || first;
    const currency = form.dataset.currency || 'MAD';
    const pair = offerTotals(form, [first, second]);
    const total = offerTotals(form, selected);
    const isPantalon = form.dataset.offerLabel === 'pantalon';
    const write = (selector, value) => { const target = form.querySelector(selector); if (target) target.textContent = value; };
    write('[data-offer-single]', isPantalon ? `${Number(first?.price || 0) / 100} DH` : formatMoney(first?.price, currency));
    write('[data-offer-regular]', formatMoney(pair.normal, currency));
    write('[data-offer-pair]', isPantalon ? `${pair.total / 100} DH` : formatMoney(pair.total, currency));
    write('[data-offer-saving]', isPantalon ? `-${pair.discount / 100} DH` : `Économisez ${formatMoney(pair.discount, currency)}`);
    const saving = form.querySelector('[data-offer-saving]');
    const regular = form.querySelector('[data-offer-regular]');
    if (saving) saving.hidden = !pair.discount;
    if (regular) regular.hidden = !pair.discount;
    write('[data-order-total]', selected.every(Boolean) ? formatMoney(total.total, currency) : 'Indisponible');
    form.elements.quantity.value = String(selected.length);
    updateOrderRecap(form);
    const stockOK = hasOrderStock(form, selected);
    write('[data-order-stock]', stockOK ? 'Disponible — paiement à la livraison' : 'Stock insuffisant ou combinaison indisponible. Modifiez votre sélection.');
    const info = form.closest('product-info');
    if (first && info) {
      const price = info.querySelector('.product__info-container .price-item--regular');
      if (price) price.textContent = formatMoney(first.price, currency);
      updateDetails(info.querySelector('[data-wearse-product-details]'), first, form.dataset.sectionId);
    }
  }

  function updateVariantRows(form) {
    const variants = getDirectOrderVariants(form);
    const rows = [...form.querySelectorAll('[data-wearse-direct-order-variant-row]')];
    let valid = true;

    rows.forEach((row) => {
      const selections = row.dataset.defaultVariant ? variants.find(v => String(v.id) === row.dataset.defaultVariant)?.options || [] : getVariantSelections(row);
      const selectedVariant = findSelectedVariant(variants, selections);
      row.dataset.variantId = selectedVariant?.id || '';
      valid = valid && Boolean(selectedVariant);

      row.querySelectorAll('select[data-option-index]').forEach((select) => {
        const optionIndex = Number(select.dataset.optionIndex);
        [...select.options].forEach((option) => {
          const candidate = [...selections];
          candidate[optionIndex] = option.value;
          option.disabled = !variants.some((variant) =>
            variant.available && variant.options?.every((value, index) => index > optionIndex || value === candidate[index])
          );
        });
      });
    });

    if (form.hasAttribute('data-dynamic-offer')) {
      valid = valid && hasOrderStock(form, selectedOrderVariants(form));
      updateOfferDisplay(form);
    }
    const firstVariantId = rows[0]?.dataset.variantId || '';
    const hiddenVariant = form.querySelector('input.product-variant-id');
    if (hiddenVariant) hiddenVariant.value = firstVariantId;
    const submit = form.querySelector('button[type="submit"]');
    if (submit) { submit.disabled = !valid; submit.setAttribute('aria-disabled', String(!valid)); }
    const next = form.querySelector('[data-order-continue]');
    if (next) next.disabled = !valid;
    return valid;
  }

  function renderVariantRows(form) {
    const variants = getDirectOrderVariants(form);
    const container = form.querySelector('[data-wearse-direct-order-variant-groups]');
    const optionNames = String(form.dataset.optionNames || '').split('||');
    const quantity = Math.max(1, Number(form.elements.offer_quantity?.value || 1));
    if (!container || !variants.length) return;

    const previous = [...container.querySelectorAll('[data-wearse-direct-order-variant-row]')].map(getVariantSelections);
    const initialId = form.querySelector('input.product-variant-id')?.value;
    const fallback = variants.find(variant => variant.available && String(variant.id) === initialId) || variants.find((variant) => variant.available) || variants[0];
    const optionValues = optionNames.map((_, optionIndex) => [
      ...new Set(variants.map((variant) => variant.options?.[optionIndex]).filter(Boolean)),
    ]);
    container.replaceChildren();

    for (let itemIndex = 0; itemIndex < quantity; itemIndex += 1) {
      const selections = previous[itemIndex]?.length ? previous[itemIndex] : [...(fallback.options || [])];
      const row = document.createElement('fieldset');
      row.className = 'wearse-direct-order__variant-row';
      row.dataset.wearseDirectOrderVariantRow = '';
      const legend = document.createElement('legend');
      legend.textContent = form.hasAttribute('data-dynamic-offer') ? `${form.dataset.offerLabel === 'paire' ? 'Paire' : 'Article'} ${itemIndex + 1}` : `${form.dataset.productTitle} ${itemIndex + 1}`;
      row.append(legend);

      const optionGrid = document.createElement('div');
      optionGrid.className = 'wearse-direct-order__variant-options-grid';

      optionNames.forEach((optionName, optionIndex) => {
        if (form.hasAttribute('data-dynamic-offer') && optionName === 'Title' && optionValues[optionIndex].length === 1 && optionValues[optionIndex][0] === 'Default Title') { row.dataset.defaultVariant = fallback.id; return; }
        const group = document.createElement('label');
        group.className = 'wearse-direct-order__option';
        const title = document.createElement('span');
        title.className = 'wearse-direct-order__option-title';
        title.textContent = /colou?r/i.test(optionName) ? 'Couleur' : optionName;
        const select = document.createElement('select');
        select.name = `item_${itemIndex + 1}_option_${optionIndex + 1}`;
        select.dataset.optionIndex = String(optionIndex);
        select.setAttribute('aria-label', `${title.textContent} - ${form.dataset.productTitle} ${itemIndex + 1}`);

        optionValues[optionIndex].forEach((value, valueIndex) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          option.selected = selections[optionIndex] === value;
          select.append(option);
        });

        group.append(title, select);
        optionGrid.append(group);
      });
      row.append(optionGrid);
      container.append(row);
    }

    updateVariantRows(form);
  }

  function updateDirectOrder(form, variant) {
    if (!form || !variant) return;

    const submit = form.querySelector('button[type="submit"]');
    const submitText = form.querySelector('[data-wearse-direct-order-submit-text]');

    if (submit) {
      submit.disabled = !variant.available;
      submit.setAttribute('aria-disabled', String(!variant.available));
    }
    if (submitText) submitText.textContent = variant.available ? 'Commander maintenant' : 'Indisponible';
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const productInfo = document.querySelector(`product-info[data-section="${data.sectionId}"]`) || document.querySelector('product-info');
      updateDetails(productInfo?.querySelector('[data-wearse-product-details]'), data.variant, data.sectionId);
      updateDirectOrder(productInfo?.querySelector('[data-wearse-direct-order]'), data.variant);
    });
  }

  function initDirectOrders(root = document) {
  root.querySelectorAll('[data-wearse-direct-order]').forEach((form) => {
    if (form.dataset.orderInitialized) return;
    form.dataset.orderInitialized = 'true';
    renderVariantRows(form);
    showOrderStep(form, 1, false);
    form.querySelector('[data-order-continue]')?.addEventListener('click', () => continueOrder(form));
    form.querySelector('[data-order-back]')?.addEventListener('click', () => {
      if (form.dataset.submitting === 'true') return;
      form.querySelector('[data-wearse-direct-order-feedback]').hidden = true;
      showOrderStep(form, 1);
    });
    form.addEventListener('change', (event) => {
      if (event.target.name === 'offer_quantity') renderVariantRows(form);
      else if (event.target.matches('[data-option-index]')) {
        if (form.hasAttribute('data-dynamic-offer')) {
          const row = event.target.closest('[data-wearse-direct-order-variant-row]');
          const selections = getVariantSelections(row);
          const changedIndex = Number(event.target.dataset.optionIndex);
          const matching = getDirectOrderVariants(form).find(v => v.available && v.options.every((value, index) => index > changedIndex || value === selections[index]));
          if (matching) row.querySelectorAll('select[data-option-index]').forEach(select => { if (Number(select.dataset.optionIndex) > changedIndex) select.value = matching.options[Number(select.dataset.optionIndex)]; });
        }
        updateVariantRows(form);
      }
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const feedback = form.querySelector('[data-wearse-direct-order-feedback]');
      const submit = form.querySelector('button[type="submit"]');
      const submitText = form.querySelector('[data-wearse-direct-order-submit-text]');

      if (form.dataset.submitting === 'true') return;
      if (form.querySelector('[data-order-step]') && form.dataset.orderStep !== '2') { continueOrder(form); return; }
      if (form.hasAttribute('data-dynamic-offer')) {
        for (const name of ['full_name', 'city']) {
          const input = form.elements[name];
          input.setCustomValidity(input.value.trim() ? '' : 'Veuillez renseigner ce champ.');
        }
        const phone = form.elements.phone;
        const normalized = phone.value.replace(/[\s().-]/g, '').replace(/^(?:\+212|00212|212)/, '').replace(/^0/, '');
        phone.setCustomValidity(/^[5-7]\d{8}$/.test(normalized) ? '' : 'Saisissez un numéro marocain valide (ex. 06 12 34 56 78).');
      }
      if (!form.reportValidity()) return;
      if (form.hasAttribute('data-dynamic-offer')) {
        form.dataset.submitting = 'true';
        submit.disabled = true;
        try {
          const url = new URL(form.dataset.productUrl, location.origin);
          url.searchParams.set('section_id', form.dataset.sectionId);
          const response = await fetch(url, { cache: 'no-store' });
          if (!response.ok) throw new Error('Stock unavailable');
          const fresh = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-dynamic-offer]');
          if (!fresh) throw new Error('Product unavailable');
          for (const selector of ['[data-wearse-direct-order-variants]', '[data-wearse-inventory]']) form.querySelector(selector).textContent = fresh.querySelector(selector).textContent;
          const oldTotal = form.querySelector('[data-order-total]').textContent;
          const stockOK = updateVariantRows(form);
          if (!stockOK) throw new Error('La sélection dépasse le stock disponible.');
          if (oldTotal !== form.querySelector('[data-order-total]').textContent) throw new Error('Le prix a été actualisé. Vérifiez le total puis confirmez votre commande.');
        } catch (error) {
          feedback.textContent = error.message.startsWith('La sélection') || error.message.startsWith('Le prix') ? error.message : 'Impossible de vérifier la disponibilité. Veuillez réessayer.';
          feedback.hidden = false;
          feedback.classList.add('is-error');
          delete form.dataset.submitting;
          updateVariantRows(form);
          return;
        }
      }

      form.dataset.submitting = 'true';
      submit.disabled = true;
      submit.classList.add('loading');
      if (submitText) submitText.textContent = 'Envoi en cours...';
      if (feedback) feedback.hidden = true;

      try {
        await submitProductOrder(form);
        if (feedback) {
          feedback.textContent = 'Votre commande Shopify est confirmée. Redirection en cours…';
          feedback.classList.remove('is-error');
          feedback.hidden = false;
        }
      } catch (error) {
        if (feedback) {
          feedback.textContent = error?.message || "Une erreur s'est produite. Veuillez réessayer.";
          feedback.classList.add('is-error');
          feedback.hidden = false;
        }
      } finally {
        delete form.dataset.submitting;
        submit.disabled = !updateVariantRows(form);
        submit.classList.remove('loading');
        if (submitText) submitText.textContent = submit.disabled ? 'Indisponible' : 'Commander maintenant';
      }
    });
  });

  }
  initDirectOrders();
  document.addEventListener('shopify:section:load', event => initDirectOrders(event.target));

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-product-media-step]');
    if (!button) return;
    const gallery = button.closest('.product__media-wrapper').querySelector('media-gallery');
    const items = [...gallery.querySelectorAll('[id^="Slide-"][data-media-id]')];
    const index = items.findIndex(item => item.classList.contains('is-active'));
    const next = items[(Math.max(0, index) + Number(button.dataset.productMediaStep) + items.length) % items.length];
    if (next) gallery.setActiveMedia(next.dataset.mediaId, false);
  });
  const desktopHoverQuery = window.matchMedia('(hover: hover) and (min-width: 990px)');

  document.querySelectorAll('.product__media').forEach((media) => {
    media.addEventListener('mousemove', (event) => {
      if (!desktopHoverQuery.matches || !media.closest('product-info[data-zoom-on-hover]')) return;
      const image = media.querySelector('img');
      if (!image) return;

      const rect = media.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      image.style.transformOrigin = `${x}% ${y}%`;
      image.classList.add('is-zoomed');
    });

    media.addEventListener('mouseleave', () => {
      const image = media.querySelector('img');
      if (!image) return;
      image.classList.remove('is-zoomed');
      image.style.transformOrigin = '';
    });
  });

  document.querySelectorAll('[data-wearse-product-tabs]').forEach((tabsRoot) => {
    const tabs = [...tabsRoot.querySelectorAll('[data-wearse-product-tab]')];
    const panels = [...tabsRoot.querySelectorAll('[data-wearse-product-tab-panel]')];

    const activateTab = (tab) => {
      const target = tab.dataset.wearseProductTab;
      tabs.forEach((item) => {
        const isActive = item === tab;
        item.setAttribute('aria-selected', String(isActive));
        item.tabIndex = isActive ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.dataset.wearseProductTabPanel !== target;
      });
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activateTab(tab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const offset = event.key === 'ArrowRight' ? 1 : -1;
        const nextTab = tabs[(index + offset + tabs.length) % tabs.length];
        activateTab(nextTab);
        nextTab.focus();
      });
    });
  });

  document.querySelectorAll('[data-wearse-customer-gallery]').forEach((gallery) => {
    const viewport = gallery.querySelector('[data-wearse-gallery-viewport]');
    const firstSlide = gallery.querySelector('.wearse-customer-gallery__slide');
    if (!viewport || !firstSlide) return;

    const move = (direction) => {
      const track = gallery.querySelector('.wearse-customer-gallery__track');
      const gap = Number.parseFloat(getComputedStyle(track).gap) || 0;
      viewport.scrollBy({ left: direction * (firstSlide.getBoundingClientRect().width + gap), behavior: 'smooth' });
    };

    gallery.querySelector('[data-wearse-gallery-prev]')?.addEventListener('click', () => move(-1));
    gallery.querySelector('[data-wearse-gallery-next]')?.addEventListener('click', () => move(1));
  });
})();
